import type { InboundRtpVideoStats } from "../../connection-stats";
import type { VideoComparisonSample } from "./video-latency-control";

/** Rates use the report's monotonic timestamp; absent/reset counters stay unknown. */
export function videoSampleBetween(
  previous: InboundRtpVideoStats | null,
  current: InboundRtpVideoStats
): VideoComparisonSample {
  const delays = videoLatencyBetween(previous, current);
  const elapsed = (current.timestamp ?? NaN) - (previous?.timestamp ?? NaN);
  const intervalMs =
    previous?.id === current.id && Number.isFinite(elapsed) && elapsed > 0 ? elapsed : null;
  function delta(key: "framesDecoded" | "framesDropped") {
    const before = previous?.[key];
    const after = current[key];
    if (
      intervalMs === null ||
      typeof before !== "number" ||
      typeof after !== "number" ||
      !Number.isFinite(before) ||
      !Number.isFinite(after) ||
      before < 0 ||
      after < before
    )
      return null;
    return after - before;
  }
  const decoded = delta("framesDecoded");
  return {
    ...delays,
    intervalMs,
    decodedFps: decoded === null ? null : (decoded * 1000) / intervalMs!,
    droppedDelta: delta("framesDropped"),
  };
}

/** Cumulative WebRTC counters must be differenced to measure the latest interval. */
export function videoLatencyBetween(
  previous: InboundRtpVideoStats | null,
  current: InboundRtpVideoStats
): { jitterBufferMs: number | null; decodeMs: number | null } {
  function averageMs(
    total: "jitterBufferDelay" | "totalDecodeTime",
    count: "jitterBufferEmittedCount" | "framesDecoded"
  ) {
    if (!previous || previous.id !== current.id) return null;
    const beforeTotal = previous[total];
    const afterTotal = current[total];
    const beforeCount = previous[count];
    const afterCount = current[count];
    if (
      ![beforeTotal, afterTotal, beforeCount, afterCount].every(
        (n) => typeof n === "number" && Number.isFinite(n) && n >= 0
      )
    )
      return null;
    const samples = afterCount! - beforeCount!;
    const seconds = afterTotal! - beforeTotal!;
    if (samples <= 0 || seconds < 0) return null;
    return (seconds * 1000) / samples;
  }
  return {
    jitterBufferMs: averageMs("jitterBufferDelay", "jitterBufferEmittedCount"),
    decodeMs: averageMs("totalDecodeTime", "framesDecoded"),
  };
}
